/*!
	jquery.dynatree.js
	Dynamic tree view control, with support for lazy loading of branches.

	Copyright (c) 2008  Martin Wendt (http://wwWendt.de)
	Licensed under the MIT License (MIT-License.txt)

	A current version and some documentation should be available at
		http://dynatree.googlecode.com/

	Let me know, if you find bugs or improvements (martin at domain wwWendt.de).

	$Rev: 77$
	$Date: 2008-11-02 19:04:54$

	@depends: jquery.js
	@depends: ui.core.js
	@depends: jquery.cookie-custom.js

RF 11/2008-01/2009:
 - modified tree expander/lines to a more compact rendering style
   (lines and expanders images are now separated)
 - added multiSelect option (click on category toggles selection)
 - added nodeHTMLAppend option (html code to add after node titles)
 - converted keys in cookie to integer (so that jQuery.inArray() works..)
 - added dtnode.data.disabled attribute
RF 2009-10-12:
 - default is NO icon (not the 'default' icon)
RF 2010-08-02:
 - updated to ui 1.8.x conventions
RF 2010-09-05:
 - removed some unused options & code for optimization purposes
RF 2011-02-18:
 - added title attribute to nodes (for improved support for over-length / clipped node texts)
RF 2011-10-03:
 - added cookieOpts option
RF 2012-01-16:
 - added DynaTreeNode::_delNode(key) function
RF 2012-09-08:
 - added DynaTree::enableMultiSelect(bEnable)
 - added DynaTree::unselectAllNodes()
RF 2012-09-21:
 - added autoSelectChildren option
RF 2012-11-12:
 - trigger onSelect() callback, even if a document was selected a second time (allows view updates, as most of the time expected from user)
RF 2013-04-29:
 - added DynaTree::getSelectedKeys()
RF 2014-03-25:
 - added DynaTree::selectSingleNodeByKey(key)
RF 2016-12-29:
 - added onDisabledClicked() callback
RF 2017-01-04:
 - added DynaTreeNode::expand() and DynaTreeNode::collapse()
 - node rendering optimized
 - added onAfterUpdateNode callback
 - removed autoCollapse option
RF 2017-01-06:
 - replaced onAfterUpdateNode with more economical onAfterRecursiveNodeUpdate
RF 2017-03-29:
 - ...
RF 2018-02-22:
 - added destroy methods to avoid memory leaks
RF 2019-09-23:
 - added DynaTree::unselectKey(key)
 - added DynaTree::setSelection(keys,noCallEvent)
RF 2020-08-01:
 - compress cookie values
RF 2020-11-27:
 - optimize rendering of deep hierarchies and many nodes
 - deprecated onUpdateNode and onRenderNode
RF 2020-12-02:
 - node rendering & updates rewritten again
RF 2021-01-09:
 - converted to es6 module
*/

import { arr_inArray, elementHasClassName, toggleClass } from './util.js';
import { jQuery } from '../other/jquery-ui-hooked.js';

import { MDLocale } from './locale-impl.js';


(function($,window) {

	/*************************************************************************
	 *	Common functions for extending classes etc.
	*	Borrowed from prototype.js
	*/
	// TODO: remove Class
	var Class = {
		create: function() {
			return function() {
				this.initialize.apply(this, arguments);
			};
		}
	};

	/*************************************************************************
	 *	DynaTreeNode
	*/
	var DynaTreeNode = Class.create();

	DynaTreeNode.prototype = {

		initialize: function( tree, data, parent ) {
			this.tree = tree;
			this.data = ( typeof data == 'string' )
				? { title: data }
				: Object.assign( {}, $.ui.dynatree.nodedatadefaults, data )
			;
			this.parent = parent;
			this.div = null; // not yet created
			this.span = null; // not yet created
			this.spanPrependClasses = null;
			this.aChilds = null; // no subnodes yet
			this.bExpanded = !!data.expand; // Collapsed by default

			if( !tree.initCookie && tree.options.persist ) {
				// Initialize expand state from cookie
				this.bExpanded = arr_inArray( this.data.key, tree.expandedKeys ) >= 0;
			} else if( tree.initCookie && this.bExpanded ) {
				// Initialize cookie from status stored in data.expanded
				tree._changeExpandList( this.data.key, true, false );
			}
		},

		getSpanPartialIndentClasses: function() {
			if( this.parent===null ) return [];
			var tpac = this.parent.aChilds;
			return [
				'uidt-1 ' + ( tpac.length===1 || tpac[tpac.length-1]===this ? 'uidt-l uidt-ne' : 'uidt-l uidt-nes' ),
				'uidt-2 ' + ( this.aChilds===null || this.aChilds.length===0 ? 'uidt-n' : ( this.bExpanded ? 'uidt-e uidt-m' : 'uidt-e uidt-p' ) )
			];
		},

		getSpanWholeIndentHtml: function() {
			var i;
			var html = '';
			if( this.spanPrependClasses!=null ) {
				for( i=0; i<this.spanPrependClasses.length; ++i ) html += '<div class="'+this.spanPrependClasses[i]+'"></div>';
			}
			var partialIndentClasses = this.getSpanPartialIndentClasses();
			for( i=0; i<partialIndentClasses.length; ++i ) html += '<div class="'+partialIndentClasses[i]+'"></div>';
			return html;
		},

		getSpanTitleHtml: function( tto ) {
			var quoted_title = this.data.title;
			var attr_quoted_title = quoted_title.replace('"','&quot;');
			var attr_href = tto.nodeHREF.replace('%key%',this.data.key);
			var nha = tto.nodeHTMLAppend==='' ? '' : tto.nodeHTMLAppend.replace('%key%',this.data.key);
			return '<a href="'+attr_href+'" class="ui-dynatree-title" title="'+attr_quoted_title+' (ID '+this.data.key+')">' + quoted_title + nha + '</a>';
		},


		setupSpan: function( vis_state ) {
			if( vis_state ) {
				var tto = this.tree.options;

				if( !this.span.hasChildNodes() ) {
					// full update
					this.span.innerHTML = this.getSpanWholeIndentHtml() + this.getSpanTitleHtml(tto);
				} else {
					// update parts only
					var indentClasses = this.getSpanPartialIndentClasses();
					var elem = this.span.lastElementChild;	// <a>
					if( elem ) {
						if( (elem=elem.previousElementSibling) ) {	// <div class="uidt-2 ...">
							elem.className = indentClasses[1];
							if( (elem=elem.previousElementSibling) ) { // <div class="uidt-1 ...">
								elem.className = indentClasses[0];
							}
						}
					}
				}

				toggleClass(this.span,tto.classnames.disabled,this.data.disabled);

				var isSelected = elementHasClassName(this.span,tto.classnames.selected);
				toggleClass(this.span,tto.classnames.selected,isSelected||this.tree.tnSelected===this);

				if( this.div.style.display==='none' ) {
					// make div visible
					this.div.style.display = '';
				}
			} else {
				this.div.style.display = 'none';
				// clear elements contents - avoid memory hogs
				while( this.span.lastChild ) this.span.lastChild.remove();	//this.span.removeChild(this.span.lastChild);
			}
		},



		getSpanPrependClasses: function() {
			var res, pp, pac;
			var tp = this.parent;

			if( tp.spanPrependClasses===null ) {
				res = [];
				while( (pp=tp.parent)!==null ) {
					pac = pp.aChilds;
					res.unshift( 'uidt-0 ' + ( (pac.length===1||pac[pac.length-1]===tp) ? 'uidt-l' : 'uidt-l uidt-ns' ) );
					tp = pp;
				}
			} else {
				res = tp.spanPrependClasses.slice();
				if( (pp=tp.parent)!==null ) {
					pac = pp.aChilds;
					res.push( 'uidt-0 ' + ( (pac.length===1||pac[pac.length-1]===tp) ? 'uidt-l' : 'uidt-l uidt-ns' ) );
				}
			}

			var prevCnt = 0;
			for( var i=0; i<res.length; ++i ) {
				var curCnt;
				const resIStr = res[i];
				if( resIStr==='uidt-0 uidt-l uidt-ns' ) {
					curCnt = 1;
				} else if( !resIStr.startsWith('uidt-0 uidt-l uidt-ns') ) {
					prevCnt = 0;
					continue;
				} else {
					curCnt = parseInt( resIStr.charAt(resIStr.length-1), 10 ); // last char (1,2,3,4)
					if( isNaN(curCnt) ) {
						curCnt = 1;
					}
				}
				var combinedWithPrevCnt = prevCnt + curCnt;
				if( prevCnt > 0 ) {
					if( combinedWithPrevCnt > 4 ) {
						// dont combine with previous, maybe with next one
						combinedWithPrevCnt = curCnt >= 4 ? 0 : curCnt;
					} else if( combinedWithPrevCnt > 1 ) {
						// combine with previous one
						res[i-1] = 'uidt-0 uidt-l uidt-ns'+combinedWithPrevCnt;
						res.splice(i,1);
						--i;
					}
				}
				prevCnt = combinedWithPrevCnt;
			}

			return res;
		},

		initRenderState: function() {
			var tto = this.tree.options;

			this.div = window.document.createElement('div');
			this.div.className = tto.classnames.div;

			if( this.span===null ) {
				this.span = window.document.createElement('span');
				this.span.id = tto.idPrefix+this.data.key;
				this.span.className = tto.classnames.document;
				this.span.dtnode = this;
			}
			this.div.appendChild(this.span);

			var tp = this.parent;
			if( tp===null ) {
				this.tree.divRoot = this.div;
				this.div.className = tto.classnames.container; // ???
				this.tree.divTree.appendChild(this.tree.divRoot);
				return;
			}

			var tpac = tp.aChilds;
			var tpac_len = tpac.length;
			if( tpac_len===1 || this===tpac[tpac_len-1] ) {
				// append to parents div
				tp.div.appendChild(this.div);
			} else {
				// insert at specific position into parents div
				var insb4idx = arr_inArray(this,tpac) + 1;
				var insertBeforeNode = insb4idx===-1 || insb4idx>=tpac_len ? null : tpac[insb4idx];
				if( insertBeforeNode==null || insertBeforeNode.div==null ) tp.div.appendChild(this.div);
				else tp.div.insertBefore(this.div,insertBeforeNode.div);
			}

			this.spanPrependClasses = this.getSpanPrependClasses();
		},

		removeRenderState: function( removeSpan ) {
			// remove childrens render state
			if( this.aChilds ) {
				for( var i=this.aChilds.length-1; i>=0; --i ) this.aChilds[i].removeRenderState(false);
			}
			if( this.div!==null ) {
				if( this.span!==null ) this.span.remove();		//this.div.removeChild(this.span);
				if( this.div.parentNode ) this.div.remove();	//this.div.parentNode.removeChild(this.div);
				this.spanPrependClasses = null;
				this.div = null;
				if( removeSpan ) this.span = null;
			}
		},

		// resetRenderState: function() {
		// },

		render_pass1_flat: function() {
			if( this.div==null ) this.initRenderState();
		},
		render_pass1_deep: function( bUpdateHiddenNodesToo ) {
			// depth-first search ..
			var stack = [ this ];
			while( stack.length ) {
				var curNode = stack.pop();
				if( curNode.div==null ) curNode.initRenderState();
				if( ( bUpdateHiddenNodesToo || curNode.bExpanded ) && curNode.aChilds ) {
					stack.push(...curNode.aChilds);
				}
			}
		},


		render_pass2_flat: function( vis_state ) {
			if( this.parent!==null ) this.setupSpan(vis_state);	// set node connector images, links and text
		},
		render_pass2_deep: function( bUpdateHiddenNodesToo, vis_state ) {
			if( this.parent!==null ) {
				this.setupSpan(vis_state);	// set node connector images, links and text
			}
			if( ( bUpdateHiddenNodesToo || this.bExpanded ) && this.aChilds ) {
				var cvis_state = vis_state && this.bExpanded;
				var clen = this.aChilds.length;
				for( var i=0; i<clen; i++ ) {
					this.aChilds[i].render_pass2_deep(bUpdateHiddenNodesToo,cvis_state);
				}
			}
		},


		update_pass2: function( bDeep, bUpdateHiddenNodesToo, vis_state ) {
			if( this.parent!==null ) {
				this.setupSpan(vis_state); // set node connector images, links and text
			}
			if( bDeep && ( bUpdateHiddenNodesToo || this.bExpanded ) && this.aChilds ) {
				var cvis_state = vis_state && this.bExpanded;
				var clen = this.aChilds.length;
				for( var i=0; i<clen; i++ ) {
					this.aChilds[i].update_pass2(true,bUpdateHiddenNodesToo,cvis_state);
				}
			}
		},

		render: function( bDeep, bUpdateHiddenNodesToo ) {
			if( bDeep ) {
				this.render_pass1_deep(bUpdateHiddenNodesToo);
				this.render_pass2_deep(bUpdateHiddenNodesToo,this.isVisible());
			} else {
				this.render_pass1_flat();
				this.render_pass2_flat(this.isVisible());
			}
		},

		update: function( bDeep, bUpdateHiddenNodesToo ) {
			if( bDeep ) {
				this.render_pass1_deep(bUpdateHiddenNodesToo);
			} else {
				this.render_pass1_flat();
			}
			this.update_pass2(bDeep,bUpdateHiddenNodesToo,this.isVisible());
			var tto = this.tree.options;
			if( tto.onAfterRecursiveNodeUpdate ) tto.onAfterRecursiveNodeUpdate.call(this,this.div,this.span);
		},

		isLastSibling: function() {
			if( this.parent===null ) return true;
			var pac = this.parent.aChilds;
			var paclen = pac.length;
			return paclen===1 || pac[paclen-1]===this;
		},

		prevSibling: function() {
			if( this.parent===null ) return null;
			var pac = this.parent.aChilds;
			var paclen = pac.length; // start with 1, so prev(first) = null
			for( var i=1; i<paclen; i++ )  if( pac[i]===this ) return pac[i-1];
			return null;
		},

		prevSiblings: function() {
			if( this.parent===null ) return [];
			var pac = this.parent.aChilds;
			var paclen = pac.length; // start with 1, so prev(first) = null
			for( var i=1; i<paclen; i++ )  {
				if( pac[i]===this ) return pac.slice(0,i);
			}
			return [];
		},

		nextSibling: function() {
			if( this.parent===null ) return null;
			var pac = this.parent.aChilds;
			var paclen1 = pac.length-1; // up to length-2, so next(last) = null
			for( var i=0; i<paclen1; i++ ) if( pac[i]===this ) return pac[i+1];
			return null;
		},

		_parentList: function(includeRoot, includeSelf) {
			var l = [];
			for( var dtn=includeSelf?this:this.parent; dtn!==null; dtn=dtn.parent ) {
				if( includeRoot || dtn.parent ) l.unshift(dtn);
			}
			return l;
		},

		_parentIdList: function(includeRoot, includeSelf) {
			var l = [];
			for( var dtn=includeSelf?this:this.parent; dtn!==null; dtn=dtn.parent ) {
				if( includeRoot || dtn.parent ) l.unshift(dtn.data.key);
			}
			return l;
		},

		isVisible: function() {
			// Return true, if all parents are expanded.
			for( var dtn=this.parent; dtn!==null; dtn=dtn.parent ) {
				if( !dtn.bExpanded ) return false;
			}
			return true;
		},

		makeVisible: function() {
			var parents = this._parentList(true,false);
			for( var i=0; i<parents.length; ++i ) {
				var pnode = parents[i];
				pnode._expand(true);
			}
		},

		focus: function() {
			this.makeVisible();
			if( this.tree.options.keyboard ) {
				// keyboard enabled = focus handling enabled,
				// keyboard disabled = no focus handling here
				try {
					$(this.span).children("a").focus();
				} catch( err ) {
					// eslint-disable-next-line no-extra-semi
					;
				}
			}
		},

		select: function( event, multiSelectSet, noCallEvent ) {

			// Select - but dont focus - this node.
			if( this.data.disabled || this.tree.isDisabled ) {
				if( !noCallEvent && this.tree.options.onDisabledClicked ) this.tree.options.onDisabledClicked.call(this.span, this);
				return; // cannot select
			}
			if( this.tree.options.multiSelect ) {
				// just ignore this.tree.tnSelected in multiSelect mode..
				this.tree.tnSelected = null;
				var cn = this.tree.options.classnames.selected;
				if( multiSelectSet != null ) {
					// just set selection..
					$(this.span).toggleClass(cn,!!multiSelectSet);
				} else {
					if( event && ( event.ctrlKey || event.altKey || event.shiftKey ) ) {
						// toggle selection
						$(this.span).toggleClass(cn);
					} else {
						// unselect all other nodes
						$('.'+cn,this.tree.divRoot).removeClass(cn);
						// select node
						$(this.span).addClass(cn);
					}
				}
				if( this.aChilds && this.aChilds.length>0 && this.tree.options.autoSelectChildren ) {
					// select/unselect all children
					var is_selected = elementHasClassName(this.span,cn);
					var clen = this.aChilds.length;
					for( var i=0; i<clen; i++ ) {
						this.aChilds[i].select(event,is_selected,true);
					}
				}
			} else {
				if( this.tree.tnSelected ) {
					//allow update after 'selecting' a second time://if( this.tree.tnSelected === this ) return;
					this.tree.tnSelected.unselect();
				}
				if( this.tree.options.selectionVisible ) this.makeVisible();
				this.tree.tnSelected = this;
				$(this.span).addClass(this.tree.options.classnames.selected);
			}
			if( !noCallEvent && this.tree.options.onSelect ) this.tree.options.onSelect.call(this.span,this);
		},

		unselect: function() {
			$(this.span).removeClass(this.tree.options.classnames.selected);
			if( this.tree.tnSelected===this ) this.tree.tnSelected = null;
		},

		enable: function() {
			this.data.disabled = false;
			$(this.span).removeClass(this.tree.options.classnames.disabled);
		},
		disable: function() {
			this.data.disabled = true;
			$(this.span).addClass(this.tree.options.classnames.disabled);
		},
		disableAll: function() {
			if( this.aChilds ) {
				var clen = this.aChilds.length;
				for( var i=0; i<clen; i++ ) this.aChilds[i].disableAll();
			}
			this.disable();
		},

		_expand: function( bExpand ) {
			if( this.bExpanded == bExpand ) return;
			this.bExpanded = bExpand;

			// Persist expand state
			if( this.tree.options.persist && this.data.key ) this.tree._changeExpandList( this.data.key, bExpand, true );

			// If current focus is now hidden, focus the first visible parent.
			// TODO: doesn't make sense here(?) we should check if the currently focused node (not <this>) is visible.
			// At the moment, _expand gets only called, after focus was set to <this>.
			if( !this.bExpanded && !this.isVisible() ) this.focus();

			// If current selection is now hidden, unselect it
			if( this.tree.options.selectionVisible && this.tree.tnSelected && !this.tree.tnSelected.isVisible() ) {
				// but save selection for later ..
				var saveTnSelected = this.tree.tnSelected;
				this.tree.tnSelected.unselect();
				this.tree.tnSelected = saveTnSelected;
			}

			// update expanded nodes
			// this.resetRenderState();
			this.update(true,true);
		},

		toggleExpand: function() {
			this._expand( !this.bExpanded );
		},

		expand: function() {
			this._expand(true);
		},
		collapse: function() {
			this._expand(false);
		},

		collapseSiblings: function() {
			if( this.parent===null ) return;
			var pac = this.parent.aChilds;
			var paclen = pac.length;
			for( var i=0; i<paclen; i++ ) {
				if( pac[i]!==this && pac[i].bExpanded ) pac[i]._expand(false);
			}
		},

		onClick: function(event) {
			if( elementHasClassName(event.target,'uidt-e') ) this.toggleExpand(); // Clicking the [+] icon always expands
			else if( elementHasClassName(event.target,'uidt-n') ) return false; // ignore click on [*] icon
			else this.select(event); // Clicking a document will select it
			this.focus();
			return false;
		},

		onKeypress: function(event) {
			var code = ( !event.charCode ) ? 1000+event.keyCode : event.charCode;
			var handled = true;
			var parents, i, sib;
			switch( code ) {
			// charCodes:
			case 43: // '+'
				if( !this.bExpanded ) this.toggleExpand();
				break;
			case 45: // '-'
				if( this.bExpanded ) this.toggleExpand();
				break;
			//~ case 42: // '*'
				//~ break;
			//~ case 47: // '/'
				//~ break;
			case 1032: // <space>
			case 32: // <space>
				this.select(event);
				break;
			// keyCodes
			case 1008: // <backspace>
				if( this.parent ) this.parent.focus();
				break;
			case 1037: // <left>
				if( this.bExpanded && this.aChilds && this.aChilds.length ) {
					this.toggleExpand();
					this.focus();
				} else if( this.parent && this.parent.parent ) {
					this.parent.focus();
				}
				break;
			case 1039: // <right>
				if( !this.bExpanded && this.aChilds && this.aChilds.length ) {
					this.toggleExpand();
					sib = this;
				} else if( this.aChilds && this.aChilds.length ) {
					sib = this.aChilds[0];
				} else {
					parents = this._parentList(false, true);
					for( i=parents.length-1; i>=0; i-- ) {
						sib = parents[i].nextSibling();
						if( sib ) break;
					}
				}
				if( sib ) sib.focus();
				break;
			case 1038: // <up>
				sib = this.prevSibling();
				while( sib && sib.bExpanded && sib.aChilds && sib.aChilds.length ) {
					sib = sib.aChilds[sib.aChilds.length-1];
				}
				if( !sib && this.parent && this.parent.parent ) sib = this.parent;
				if( sib ) sib.focus();
				break;
			case 1040: // <down>
				if( this.bExpanded && this.aChilds && this.aChilds.length ) {
					sib = this.aChilds[0];
				} else {
					parents = this._parentList(false, true);
					for( i=parents.length-1; i>=0; i-- ) {
						sib = parents[i].nextSibling();
						if( sib ) break;
					}
				}
				if( sib ) sib.focus();
				break;
			//~ case 1013: // <enter>
				//~ this.select();
				//~ break;
			default:
				handled = false;
			}
			if( handled ) return false;
		},

		onFocus: function(event) {
			if( event.type=="blur" || event.type=="focusout" ) {
				if( this.tree.options.onBlur ) this.tree.options.onBlur.call( this.span, this );
				if( this.tree.tnFocused ) $(this.tree.tnFocused.span).removeClass( this.tree.options.classnames.focused );
				this.tree.tnFocused = null;
			} else if( event.type=="focus" || event.type=="focusin") {
				if( this.tree.tnFocused && this.tree.tnFocused !== this ) $(this.tree.tnFocused.span).removeClass( this.tree.options.classnames.focused );
				this.tree.tnFocused = this;
				if( this.tree.options.onFocus ) this.tree.options.onFocus.call( this.span, this );
				$(this.tree.tnFocused.span).addClass( this.tree.options.classnames.focused );
			}
		},

		_addNode: function( data, after_id ) {
			if( !data ) return null;
			if( this.aChilds===null ) {
				this.aChilds = [];
			} else {
				for( let i=0; i<this.aChilds.length; ++i ) {
					let childNode = this.aChilds[i];
					if( childNode.data.key==data.key ) return null;
				}
			}
			this.removeRenderState(false);
			var dtnode = new DynaTreeNode(this.tree,data,this);
			if( after_id==0 ) {
				// insert before first child
				this.aChilds.unshift(dtnode);
			} else if( after_id==-1 ) {
				// insert at alphabetical order
				// TODO: require a full update instead !?
				var found = false;
				for( let i=0; i<this.aChilds.length; ++i ) {
					let childNode = this.aChilds[i];
					if( MDLocale.localized_string_compare(childNode.data.title,data.title) > 0 ) {
						this.aChilds.splice(i,0,dtnode);
						found = true;
						break;
					}
				}
				if( !found ) this.aChilds.push(dtnode);
			} else if( after_id==-2 ) {
				// insert after last child
				this.aChilds.push(dtnode);
			} else {
				// insert after specified id
				for( var i=0; i<this.aChilds.length; ++i ) {
					if( this.aChilds[i].data.key==after_id ) {
						this.aChilds.splice(i+1,0,dtnode);
						break;
					}
				}
			}
			if( data.children && data.children.length ) dtnode._internalAppend(data.children);
			if( this.parent===null ) this.bExpanded = true;
			if( this.tree.bEnableUpdate ) this.update(true,true);
			return dtnode;
		},

		_delNode: function( key ) {
			if( this.aChilds===null ) return;
			this.removeRenderState(false);
			var clen = this.aChilds.length;
			for( var i=0; i<clen; i++ ) {
				if( this.aChilds[i].data.key == key ) {
					//superfluous:..//this.aChilds[i].removeRenderState(true);
					this.aChilds[i].parent = null;
					this.aChilds.splice(i,1);
					if( this.aChilds.length===0 ) this.aChilds = null;
					break;
				}
			}
			if( this.tree.bEnableUpdate ) this.update(true,true);
		},

		_delSelf: function() {
			if( this.aChilds ) {
				var clen = this.aChilds.length;
				for( var i=0; i<clen; i++ ) {
					var cnode = this.aChilds[i];
					cnode._delSelf();
				}
				this.aChilds = null;
			}
			if( this.div!==null ) {
				if( this.span!==null ) this.span.remove();		//this.div.removeChild(this.span);
				if( this.div.parentNode ) this.div.remove();	//this.div.parentNode.removeChild(this.div);
				this.div = null;
			}
			this.spanPrependClasses = null;
			this.span = null;
			this.parent = null;
			this.tree = null;
			this.data = null;
		},

		_updNode: function( title ) {
			this.data.title = title;
			this.removeRenderState(true);
			if( this.tree.bEnableUpdate ) this.update(true,true);
		},

		_internalAppend: function(obj) {
			if( Array.isArray(obj) ) {
				var ol=obj.length;
				if( ol ) {
					if( this.aChilds===null ) this.aChilds = [];
					for( var i=0; i<ol; i++ ) {
						var data = obj[i];
						let dtnode = new DynaTreeNode(this.tree,data,this);
						this.aChilds.push(dtnode);
						if( data.children && data.children.length ) dtnode._internalAppend(data.children);
					}
					if( this.parent===null ) this.bExpanded = true;
				}
			} else {
				if( this.aChilds===null ) this.aChilds = [];
				let dtnode = new DynaTreeNode(this.tree,obj,this);
				this.aChilds.push(dtnode);
				if( obj.children && obj.children.length ) dtnode._internalAppend(obj.children);
				if( this.parent===null ) this.bExpanded = true;
			}
		},

		append: function(obj) {
			if( !obj ) return;
			var prevEUFlag = this.tree.bEnableUpdate;
			if( prevEUFlag ) this.tree.bEnableUpdate = false;
			this._internalAppend(obj);
			if( prevEUFlag ) this.tree.enableUpdate(prevEUFlag);
		},

		visit: function( cb ) {
			cb.call(this,this.div,this.span);
			if( this.aChilds ) {
				var clen = this.aChilds.length;
				for( var i=0; i<clen; i++ ) this.aChilds[i].visit(cb);
			}
		},
		visitChildren: function( cb ) {
			if( this.aChilds ) {
				var clen = this.aChilds.length;
				for( var i=0; i<clen; i++ ) this.aChilds[i].visit(cb);
			}
		},

		visit2_1: function( cb, vis_state ) {
			cb.call(this,this.div,this.span,vis_state);
			if( !this.aChilds ) return;
			const cvis_state = vis_state && this.bExpanded;
			const clen = this.aChilds.length;
			if( typeof cvis_state === 'undefined' ) {
				for( let i=0; i<clen; i++ ) {
					const child = this.aChilds[i];
					child.visit2_1(cb,child.isVisible());
				}
			} else {
				for( let i=0; i<clen; i++ ) {
					this.aChilds[i].visit2_1(cb,cvis_state);
				}
			}
		},
		visit2: function( cb, vis_state ) {
			if( typeof vis_state === 'undefined' ) vis_state = this.isVisible();
			this.visit2_1(cb,vis_state);
		},

		visitChildren2: function( cb, vis_state ) {
			if( typeof vis_state === 'undefined' ) vis_state = this.isVisible();
			if( this.aChilds ) {
				const cvis_state = vis_state && this.bExpanded;
				const clen = this.aChilds.length;
				if( typeof cvis_state === 'undefined' ) {
					for( let i=0; i<clen; i++ ) {
						const child = this.aChilds[i];
						child.visit2_1(cb,child.isVisible());
					}
				} else {
					for( let i=0; i<clen; i++ ) {
						this.aChilds[i].visit2_1(cb,cvis_state);
					}
				}
			}
		},


		isRoot: function() {
			return this === this.tree.tnRoot;
		},

		// --- end of class
		lastentry: undefined
	};

	/*************************************************************************
	 * class DynaTree
	 */

	var DynaTree = Class.create();

	DynaTree.prototype = {
		// constructor
		initialize: function( id, options ) {
			// instance members
			this.options = options;
			this.tnSelected = null;
			this.bEnableUpdate = false;
			this.isDisabled = false;
			// list of expanded nodes (only maintained in persist mode)
			this.expandedKeys = [];

			this.initCookie = false;
			if( this.options.persist ) {
				// Requires jquery.cookie-custom.js:
				var cookieVal = $.cookie(this.options.cookieId);
				if( cookieVal ) {
					cookieVal = cookieVal.replace(/_/g,',');							// "," is encoded "%2C", but "_" is encoded "_"
					this.expandedKeys = cookieVal.split(",");
					for( var k in this.expandedKeys ) {
						if( !this.expandedKeys.hasOwnProperty(k) ) continue;
						this.expandedKeys[k] = parseInt(this.expandedKeys[k],10);
					}
				} else {
					this.initCookie = true; // Init first time persistence from node.data
				}
			}

			this.detached_count = 0;

			// find container element
			this.divTree = window.document.getElementById(id);
			// create the root element
			this.tnRoot = new DynaTreeNode( this, {title:this.options.title,key:'root'}, null );
			this.tnRoot.render(false,false);
			this.divRoot = this.tnRoot.div;
			this.divRoot.className = this.options.classnames.container;
			this.tnRoot.span.style.display = 'none';
			this.divTree.appendChild(this.divRoot);
		},


		// member functions

		_changeExpandList: function(key, bAdd, bWriteCookie) {
			// Add or remove key from expand-list.
			if( !key ) return false;
			var n = this.expandedKeys.length;
			if( bAdd ) {
				if( arr_inArray(key,this.expandedKeys) == -1 ) this.expandedKeys.push(key);
			} else {
				this.expandedKeys = $.grep( this.expandedKeys, function(e) {
					return e!=key;
				} );
			}
			if( bWriteCookie ) {
				var cookieVal = this.expandedKeys.join(",");
				cookieVal = cookieVal.replace(/,/g,'_');								// "," is encoded "%2C", but "_" is encoded "_"
				$.cookie(this.options.cookieId,cookieVal,this.options.cookieOpts);
			}
			return n != this.expandedKeys.length;
		},

		redraw: function() {
			this.tnRoot.render(true,true);
		},

		getRoot: function() {
			return this.tnRoot;
		},

		getNodeByKey: function( key ) {
			if( this.detached_count===0 ) {
				let el = window.document.getElementById(this.options.idPrefix+key);
				return el && el.dtnode ? el.dtnode : null;
			}
			let el = $('#'+this.options.idPrefix+key,this.divRoot);
			return el.length && el[0].dtnode ? el[0].dtnode : null;
		},

		getSelectedNode: function() {
			return this.tnSelected;
		},

		getSelectedKeys: function() {
			var selected_keys = [];
			var cn = this.options.classnames.selected;
			$('.'+cn,this.divRoot).each( function() {
				if( this.dtnode && this.dtnode.data && this.dtnode.data.hasOwnProperty('key') ) selected_keys.push(this.dtnode.data.key);
			} );
			return selected_keys;
		},

		selectKey: function( key, noCallEvent ) {
			var dtnode = this.getNodeByKey(key);
			if( !dtnode ) {
				if( this.tnSelected ) this.tnSelected.unselect();
				return null;
			}
			dtnode.focus();
			dtnode.select( null, null, noCallEvent );
			return dtnode;
		},

		unselectKey: function( key ) {
			var dtnode = this.getNodeByKey(key);
			if( !dtnode ) return null;
			dtnode.unselect();
			return dtnode;
		},

		detachTreeNodes: function() {
			if( ++this.detached_count === 1 ) {
				$(this.divRoot).detach();
			}
		},
		reattachTreeNodes: function() {
			if( --this.detached_count <= 0 ) {
				this.divTree.appendChild(this.divRoot);
				this.detached_count = 0;
			}
		},
		getTreeNodesDetached: function() {
			return this.detached_count > 0;
		},

		enableUpdate: function( bEnable ) {
			if( this.bEnableUpdate==bEnable ) return bEnable;
			this.bEnableUpdate = bEnable;
			if( bEnable ) {
				this.detachTreeNodes();
				this.tnRoot.render(true,true);
				this.reattachTreeNodes();
			}
			return !bEnable; // return previous value
		},

		enableMultiSelect: function( bEnable, bAutoSelectChildren ) {
			// NOTE: this will not change current selection, it just sets the option
			// -> caller has to care about current selection!
			this.options.multiSelect = !!bEnable;
			this.options.autoSelectChildren = !!bAutoSelectChildren;
		},

		unselectAllNodes: function() {
			// console.time('unselectAllNodes');
			//this.detachTreeNodes();

			var cn = this.options.classnames.selected;

			$('.'+cn,this.divRoot).removeClass(cn);
			// var nodes = $('.'+cn,this.divRoot);
			// $(nodes.get().reverse()).removeClass(cn);

			this.tnSelected = null;

			//this.reattachTreeNodes();
			// console.timeEnd('unselectAllNodes');
		},

		selectSingleNodeByKey: function( key ) {
			this.unselectAllNodes();
			this.selectKey(key,true);
		},

		setSelection: function( keys, noCallEvent ) {
			this.unselectAllNodes();
			var i, cid, dtnode;
			for( i=0; i<keys.length; ++i ) {
				cid = keys[i];
				dtnode = this.getNodeByKey(cid);
				if( dtnode!==null ) dtnode.select(null,this.options.multiSelect,noCallEvent);
			}
		},

		destroy: function() {
			this.tnRoot._delSelf();
			if( this.divRoot && this.divRoot.parentNode && this.divRoot.parentNode===this.divTree ) this.divRoot.remove();	//this.divTree.removeChild(this.divRoot);
			this.tnRoot = this.tnSelected = this.tnFocused = null;
			this.divTree = this.divRoot = null;
		},

		// --- end of class
		lastentry: undefined
	};

	/*************************************************************************
	 * widget $(..).dynatree
	 */

	//(function($) {

	function _getNodeFromElement( el ) {
		var iMax = 4;
		do {
			if( !el ) return null;
			if( el.dtnode ) return el.dtnode;
			el = el.parentNode;
		} while( iMax-- );
		return null;
	}

	function fnClick( event ) {
		var dtnode = _getNodeFromElement(event.target);
		return dtnode ? dtnode.onClick(event) : false;
	}

	function fnKeyHandler( event ) {
		// Handles keydown and keypressed, because IE and Safari don't fire keypress for cursor keys.
		var dtnode = _getNodeFromElement(event.target);
		if( !dtnode ) return false;
		// ...but Firefox does, so ignore them:
		if( event.type == "keypress" && event.charCode == 0 ) return;
		return dtnode.onKeypress(event);
	}

	function fnFocusHandler( event ) {
		// Handles blur and focus.
		// Fix event for IE:
		event = arguments[0] = $.event.fix( event || window.event );
		var dtnode = _getNodeFromElement(event.target);
		return dtnode ? dtnode.onFocus(event) : false;
	}

	$.widget("ui.dynatree", {

		_create: function() {
			// The widget framework supplies this.element and this.options.
			this.options.event += '.dynatree'; // namespace event

			// create lazytree
			var $this = this.element;
			var opts = this.options;

			// Attach the tree object to parent element
			this.tree = new DynaTree( $this.attr("id"), opts );
			var root = this.tree.getRoot();

			// Init tree structure
			if( opts.children ) {
				// Read structure from node array
				root.append( opts.children );
			}

			function post_create( dnttobj ) {
				// render
				dnttobj.tree.enableUpdate(true);
				// bind event handlers
				$this.bind('click',fnClick);
				if( opts.keyboard ) {
					$this.bind("keypress keydown",fnKeyHandler);
					// focus/blur don't bubble, i.e. are not delegated to parent <div> tags,
					// so we use the addEventListener capturing phase.
					// See http://www.howtocreate.co.uk/tutorials/javascript/domevents
					var div = dnttobj.tree.divTree;
					if( div.addEventListener ) {
						div.addEventListener("focus", fnFocusHandler, true);
						div.addEventListener("blur", fnFocusHandler, true);
					} else {
						div.onfocusin = div.onfocusout = fnFocusHandler;
					}
				}
				// We defined the first-time cookie from node.data, now store it
				if( dnttobj.tree.initCookie ) {
					var cookieVal = dnttobj.tree.expandedKeys.join(",");
					cookieVal = cookieVal.replace(/,/g,'_');							// "," is encoded "%2C", but "_" is encoded "_"
					$.cookie(opts.cookieId,cookieVal,opts.cookieOpts);
				}
			}

			if( opts.onAfterDelayedCreate ) {
				var dnttobj = this;
				window.setImmediate( function onAfterDelayedCreate1() {
					post_create(dnttobj);
					window.setImmediate( function onAfterDelayedCreate2() {
						opts.onAfterDelayedCreate.call(dnttobj,dnttobj.tree);
					} );
				} );
			} else {
				post_create(this);
			}

		},

		_destroy: function( ) {
			// console.log('dynatree _destroy');
			var $this = this.element;
			$this.unbind('click',fnClick);
			$this.unbind('keypress keydown',fnKeyHandler);
			var div = this.tree.divTree;
			if( div && div.removeEventListener ) {
				div.removeEventListener('focus',fnFocusHandler,true);
				div.removeEventListener('blur',fnFocusHandler,true);
			}

			this.tree.destroy();
			this.tree = null;
		},

		// --- getter methods (i.e. NOT returning a reference to $)
		getTree: function() {
			return this.tree;
		},

		getRoot: function() {
			return this.tree.getRoot();
		},

		getNodeByKey: function( key ) {
			return this.tree.getNodeByKey(key);
		},

		getSelectedNode: function() {
			return this.tree.getSelectedNode();
		},

		getSelectedKeys: function() {
			return this.tree.getSelectedKeys();
		},

		setSelection: function( keys, noCallEvent ) {
			return this.tree.setSelection(keys,noCallEvent);
		},

		// ------------------------------------------------------------------------
		lastentry: undefined
	});


	// The following methods return a value (thus breaking the jQuery call chain):
	$.ui.dynatree.getter = "getTree getRoot getSelectedNode";


	// Plugin default options:
	$.ui.dynatree.prototype.options = {
		title: "Dynatree root", // Name of the root node.
		children: null, // Init tree structure from this object array.
		onSelect: null, // Callback when a node is selected.
		onDisabledClicked: null, // Callback when a disabled node is clicked.
		onLazyRead: null, // Callback when a lazy node is expanded for the first time.
		onFocus: null, // Callback when a node receives keyboard focus.
		onBlur: null, // Callback when a node looses focus.
		onAfterRecursiveNodeUpdate: null, // RF: called after a node (and all its children) have been updated (expand/collapse/_addNode/_delNode/_updNode..)
		keyboard: true, // Support keyboard navigation. This implies focus handling on nodes. Note: No keyboard = no focus handling on nodes!
		selectionVisible: true, // Make sure, selected nodes are visible (expanded).
		idPrefix: 'ui-dynatree-id-', // Used to generate node id's like <span id="ui-dynatree-id-<key>">.
		classnames: {
			container: "ui-dynatree-container",
			disabled: "ui-dynatree-disabled",
			selected: "ui-dynatree-selected",
			document: "ui-dynatree-document",
			focused: "ui-dynatree-focused",
			div: "ui-dynatree-div"
		},
		persist: false, // Persist expand-status to a cookie
		cookieId: "ui-dynatree-cookie", // Choose a more unique name, to allow multiple trees.
		cookieOpts: {}, // options for jquery.cookie-custom
		multiSelect: false,
		autoSelectChildren: true,
		nodeHTMLAppend: '',
		onAfterDelayedCreate: null,
		nodeHREF: '#',

		lastentry: undefined
	};

	/**
	 * Reserved data attributes for a tree node.
	 */
	$.ui.dynatree.prototype.nodedatadefaults = {
		title: null, // (required) Displayed name of the node (html is allowed here)
		key: null, // May be used with select(), find(), ...
		expand: false, // Initial expand status.
		children: null, // Array of child nodes.
		disabled: false,
		lastentry: undefined
	};

})(jQuery,window);

